import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { apiFetch, setSession } from "@/lib/api";
import AuthLayout, { inputStyle, labelStyle, errorStyle } from "@/components/AuthLayout";

const COPY = {
  activate: {
    title: "Set your password",
    subtitle: "Almost there — pick a password so you can log back in any time to see your report.",
    submitLabel: "Set password & continue",
  },
  reset: {
    title: "Choose a new password",
    subtitle: "Set a new password for your account.",
    submitLabel: "Update password & log in",
  },
};

export default function SetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();

  // "checking" -> "ready" -> "submitting"; or "invalid" if the token doesn't check out
  const [status, setStatus] = useState("checking");
  const [purpose, setPurpose] = useState("activate");
  const [email, setEmail] = useState("");
  const [checkError, setCheckError] = useState("");

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      setCheckError("This link is missing its token — please use the full link from your email.");
      return;
    }
    // A check, not a redemption — safe to call even if an email scanner
    // already opened this link before the real person did.
    apiFetch(`/auth/verify?token=${encodeURIComponent(token)}`)
      .then((data) => {
        setPurpose(data.purpose === "reset" ? "reset" : "activate");
        setEmail(data.email);
        setStatus("ready");
      })
      .catch((err) => {
        setCheckError(err.message || "This link is invalid or has expired.");
        setStatus("invalid");
      });
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitError("");

    if (password.length < 8) {
      setSubmitError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setSubmitError("Passwords don't match.");
      return;
    }

    setSubmitting(true);
    try {
      const data = await apiFetch("/auth/set-password", {
        method: "POST",
        body: { token, password },
      });
      setSession(data);
      navigate(purpose === "activate" ? "/assessment" : "/dashboard", { replace: true });
    } catch (err) {
      setSubmitError(err.message || "Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  if (status === "checking") {
    return (
      <AuthLayout title="One moment…" subtitle="Checking your link.">
        <div className="pulse-dot" style={{ width: 10, height: 10, borderRadius: "50%", background: "#D4AF37" }} />
      </AuthLayout>
    );
  }

  if (status === "invalid") {
    return (
      <AuthLayout
        title="Link no longer works"
        subtitle={checkError}
        footer={
          <Link to="/forgot-password" style={{ fontSize: "0.85rem", color: "#D4AF37" }}>
            Request a new link
          </Link>
        }
      />
    );
  }

  const copy = COPY[purpose];

  return (
    <AuthLayout title={copy.title} subtitle={copy.subtitle}>
      <p style={{ fontSize: "0.85rem", color: "#A0A0B0", marginTop: "-0.5rem", marginBottom: "1.5rem" }}>
        {email}
      </p>

      <form onSubmit={handleSubmit} data-testid="set-password-form">
        <div style={{ marginBottom: "1.1rem" }}>
          <label style={labelStyle} htmlFor="sp-password">New password</label>
          <input
            id="sp-password"
            data-testid="set-password-password-input"
            type="password"
            required
            minLength={8}
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={inputStyle}
          />
        </div>

        <div style={{ marginBottom: "0.5rem" }}>
          <label style={labelStyle} htmlFor="sp-confirm">Confirm password</label>
          <input
            id="sp-confirm"
            data-testid="set-password-confirm-input"
            type="password"
            required
            minLength={8}
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            style={inputStyle}
          />
        </div>

        {submitError && <p style={errorStyle} data-testid="set-password-error">{submitError}</p>}

        <button
          type="submit"
          data-testid="set-password-submit"
          disabled={submitting}
          className="cta-button"
          style={{ width: "100%", justifyContent: "center", opacity: submitting ? 0.7 : 1, marginTop: "1.4rem" }}
        >
          {submitting ? "Saving…" : copy.submitLabel}
        </button>
      </form>
    </AuthLayout>
  );
}
